import { observer } from "mobx-react-lite";
import React from "react";
import { Navigate, useLocation, useMatch } from "react-router-dom";
import { useAuthStore } from "stores/AuthStoreProvider";
import {
  hashWithoutToken,
  RESET_PASSWORD_ROUTE,
  SET_PASSWORD_ROUTE,
} from "services/coreapi";
import { getRedirectPath } from "utils/redirect";

const HOME_ROUTE = "/";
const LOGIN_ROUTE = "/login";
const LOGOUT_ROUTE = "/logout";

interface RouteGuardProps extends React.PropsWithChildren {
  subscribeRoute?: string;
  consentRoute?: string;
  onboardingRoute?: string;
  /** Gate on age consent — users without it are sent to consentRoute. */
  requiresConsent?: boolean;
  /** Gate on an active subscription — users without one are sent to subscribeRoute. */
  requiresSubscription?: boolean;
}

/**
 * Redirect cascade for authenticated areas, evaluated top to bottom:
 * login → set password → (subscription) → (consent) → onboarding → home.
 * Gates that are disabled via props are skipped entirely — they never block
 * the checks below them.
 */
export const RouteGuard = observer(
  ({
    children,
    subscribeRoute = "/subscribe",
    consentRoute = "/consent",
    onboardingRoute = "/onboarding/welcome",
    requiresConsent = true,
    requiresSubscription = true,
  }: RouteGuardProps) => {
    const isOnLoginRoute = useMatch(LOGIN_ROUTE);
    const isOnLogoutRoute = useMatch(LOGOUT_ROUTE);
    const isOnResetPasswordRoute = useMatch(RESET_PASSWORD_ROUTE);
    const isOnSetPasswordRoute = useMatch(SET_PASSWORD_ROUTE);
    const isOnSubscribeRoute = useMatch(subscribeRoute);
    const isOnConsentRoute = useMatch(consentRoute);
    // Match the onboarding section by its first path segment, so both
    // "/onboarding" and nested routes like "/onboarding/welcome" work.
    const isOnOnboardingRoute = useMatch(
      "/" + onboardingRoute.split("/")[1] + "/*",
    );
    const location = useLocation();
    const authStore = useAuthStore();

    if (authStore.isInitializing) {
      return null;
    }

    // Signing out must work from every state: it is how a user stuck behind
    // a gate (onboarding that cannot finish, a session the platform API
    // rejects) gets out. Checked after init so the page never races the
    // session check, and before every redirect so none can capture it.
    // The reset page too: the emailed link is the user's proof, whether or
    // not this browser is logged in, and a redirect would carry its token.
    if (isOnLogoutRoute || isOnResetPasswordRoute) {
      return children;
    }

    if (!authStore.isAuthenticated) {
      if (!isOnLoginRoute) {
        // Move any auth error param out of the requested route and onto the
        // login route itself. Left inside `redirect`, it ends up as the magic
        // link's callbackURL, and a stale `error=INVALID_TOKEN` there makes
        // every later login look failed; on the login route, it drives the
        // "your link is old" banner instead.
        const searchParams = new URLSearchParams(location.search);
        const authError = searchParams.get("error");
        searchParams.delete("error");
        const search = searchParams.toString();
        const requestedRoute = location.pathname + (search ? "?" + search : "");
        const params = new URLSearchParams();
        params.append("redirect", requestedRoute);
        if (authError) {
          params.append("error", authError);
        }
        const loginRoute = LOGIN_ROUTE + "?" + params.toString();

        return <Navigate to={loginRoute} replace={true} />;
      }

      return children;
    }

    // No password yet (quiz auto-login, magic link, a session older than this
    // gate): one must be set before anything else, paying included. No skip.
    if (!authStore.hasPassword) {
      if (!isOnSetPasswordRoute) {
        // The router read the URL before the magic link's `#token=` was
        // wiped from it: copied into `redirect`, the session token would
        // reach PostHog and Sentry, URL-encoded past `redactToken`.
        const params = new URLSearchParams();
        params.append(
          "redirect",
          location.pathname + location.search + hashWithoutToken(location.hash),
        );

        return (
          <Navigate
            to={SET_PASSWORD_ROUTE + "?" + params.toString()}
            replace={true}
          />
        );
      }

      return children;
    }

    // Signed in, password set: off /set-password or /login, back to the page
    // they wanted, query string and all, before the gates below look at the
    // URL. No usable `redirect`: home, with the rest of the query string.
    // The login form leaves this redirect to the guard alone.
    if (isOnSetPasswordRoute || isOnLoginRoute) {
      const params = new URLSearchParams(location.search);
      const requestedRoute = getRedirectPath(params);
      if (requestedRoute) {
        return <Navigate to={requestedRoute} replace={true} />;
      }

      // A rejected `redirect` must not ride along to the pages after home.
      params.delete("redirect");
      const search = params.toString();
      return (
        <Navigate
          to={HOME_ROUTE + (search ? "?" + search : "")}
          replace={true}
        />
      );
    }

    if (requiresSubscription && !authStore.hasActiveSubscription) {
      if (!isOnSubscribeRoute) {
        return (
          <Navigate to={subscribeRoute + location.search} replace={true} />
        );
      }

      return children;
    }

    if (requiresConsent && !authStore.hasGrantedAgeConsent) {
      if (!isOnConsentRoute) {
        return <Navigate to={consentRoute + location.search} replace={true} />;
      }

      return children;
    }

    if (isOnConsentRoute) {
      return <Navigate to={HOME_ROUTE + location.search} replace={true} />;
    }

    if (authStore.hasCompletedOnboarding) {
      if (isOnOnboardingRoute) {
        return <Navigate to={HOME_ROUTE + location.search} replace={true} />;
      }
    } else {
      if (!isOnOnboardingRoute) {
        return (
          <Navigate to={onboardingRoute + location.search} replace={true} />
        );
      }
    }

    if (isOnSubscribeRoute) {
      return <Navigate to={HOME_ROUTE + location.search} replace={true} />;
    }

    return children;
  },
);
